<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import SMSCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SMSCampaign/SMSCampaignDialog.vue';
import RenameCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/RenameCampaignDialog.vue';
import MergeCampaignsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/MergeCampaignsDialog.vue';
import CampaignMergeBar from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignMergeBar.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import ConfirmStopCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmStopCampaignDialog.vue';
import CampaignSendsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignSendsDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const selectedIds = ref([]);
const smsCampaignDialogRef = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const renameCampaignDialogRef = ref(null);
const mergeCampaignsDialogRef = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const confirmStopCampaignDialogRef = ref(null);
const campaignSendsDialogRef = ref(null);

const SMSCampaigns = computed(() => getters['campaigns/getSMSCampaigns'].value);

const hasNoSMSCampaigns = computed(
  () => SMSCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const openCreateCampaignDialog = () => {
  smsCampaignDialogRef.value?.dialogRef.open();
};

const handleRename = campaign => {
  renameCampaignDialogRef.value.open(campaign);
};

const toggleSelection = campaign => {
  selectedIds.value = selectedIds.value.includes(campaign.id)
    ? selectedIds.value.filter(id => id !== campaign.id)
    : [...selectedIds.value, campaign.id];
};

const clearSelection = () => {
  selectedIds.value = [];
};

const selectedCampaigns = computed(() =>
  SMSCampaigns.value.filter(campaign => selectedIds.value.includes(campaign.id))
);

const handleMerge = () => {
  mergeCampaignsDialogRef.value.open(selectedCampaigns.value);
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleStop = campaign => {
  selectedCampaign.value = campaign;
  confirmStopCampaignDialogRef.value.dialogRef.open();
};

const handleViewSends = campaign => {
  selectedCampaign.value = campaign;
  campaignSendsDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.SMS.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.SMS.NEW_CAMPAIGN')"
    @click="openCreateCampaignDialog"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="!hasNoSMSCampaigns">
      <CampaignMergeBar
        v-if="selectedCampaigns.length"
        :campaigns="selectedCampaigns"
        class="mb-4"
        @merge="handleMerge"
        @clear="clearSelection"
      />
      <CampaignList
        :campaigns="SMSCampaigns"
        is-selectable
        :selected-ids="selectedIds"
        @rename="handleRename"
        @select="toggleSelection"
        @delete="handleDelete"
        @stop="handleStop"
        @view-sends="handleViewSends"
      />
    </template>
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.SMS.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.SMS.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <SMSCampaignDialog ref="smsCampaignDialogRef" />
    <RenameCampaignDialog ref="renameCampaignDialogRef" />
    <MergeCampaignsDialog
      ref="mergeCampaignsDialogRef"
      @merged="clearSelection"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmStopCampaignDialog
      ref="confirmStopCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignSendsDialog
      ref="campaignSendsDialogRef"
      :campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
